import { anchorOf, strokeWidthFor } from './geometry.js';
/**
 * Marks as SVG, as a string: the same drawing on screen (annotator.ts sets it as the
 * overlay's content), in an export (composited onto the snapshot so an agent sees what
 * the engineer drew), and anywhere else a snapshot is shown — the extension, later the
 * iOS app. Strings rather than DOM nodes so it runs without a document.
 *
 * Every mark is red with a white halo, which reads on light and dark interfaces alike;
 * the selected one is blue.
 */
export const MARK_COLOR = '#e5484d';
export const SELECTED_COLOR = '#2f6feb';
const HALO = '#ffffff';
/** How thick lines are drawn, in image pixels: in proportion to the image, and never thinner than 2px on screen. */
const lineWidth = (options) => Math.max(strokeWidthFor(options), 2 * (options.pixelsPerScreenPixel ?? 0));
const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const n = (v) => (Math.round(v * 10) / 10).toString();
const pathOf = (points) => points.map((p, i) => `${i ? 'L' : 'M'}${n(p.x)} ${n(p.y)}`).join(' ');
/** An arrow's line and its head, as one path. */
function arrowPath(from, to, head) {
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const spread = Math.PI / 7;
    const left = { x: to.x - head * Math.cos(angle - spread), y: to.y - head * Math.sin(angle - spread) };
    const right = { x: to.x - head * Math.cos(angle + spread), y: to.y - head * Math.sin(angle + spread) };
    return `${pathOf([from, to])} ${pathOf([left, to, right])}`;
}
/** The shape of one mark, before its badge. */
function shapeOf(geometry, width, color) {
    const line = (d, dash = false) => `<path d="${d}" fill="none" stroke="${HALO}" stroke-width="${width * 2.2}" stroke-linecap="round" stroke-linejoin="round" opacity="0.85"/>` +
        `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"${dash ? ` stroke-dasharray="${width * 3} ${width * 2}"` : ''}/>`;
    const rect = (b, dash) => line(pathOf([{ x: b.x, y: b.y }, { x: b.x + b.w, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, { x: b.x, y: b.y + b.h }, { x: b.x, y: b.y }]), dash);
    switch (geometry.kind) {
        case 'pin':
            return '';
        case 'region':
            return rect(geometry.box, true) + `<rect x="${n(geometry.box.x)}" y="${n(geometry.box.y)}" width="${n(geometry.box.w)}" height="${n(geometry.box.h)}" fill="${color}" opacity="0.08"/>`;
        case 'stroke': {
            const [a, b] = [geometry.points[0], geometry.points.at(-1)];
            if (geometry.tool === 'box') {
                return rect({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) }, false);
            }
            if (geometry.tool === 'arrow')
                return line(arrowPath(a, b, width * 6));
            if (geometry.tool === 'text') {
                const size = width * 9;
                const text = esc(geometry.label?.trim() || 'Text');
                return (`<text x="${n(a.x)}" y="${n(a.y)}" font-family="system-ui, sans-serif" font-weight="600" font-size="${size}" ` +
                    `fill="${color}" stroke="${HALO}" stroke-width="${width * 1.6}" paint-order="stroke" dominant-baseline="hanging">${text}</text>`);
            }
            // A pen that never moved is a dot.
            if (geometry.points.length === 1) {
                return `<circle cx="${n(a.x)}" cy="${n(a.y)}" r="${width * 1.5}" fill="${color}" stroke="${HALO}" stroke-width="${width * 0.6}"/>`;
            }
            return line(pathOf(geometry.points));
        }
    }
}
/**
 * The numbered badge beside a mark, so the note that says "2" is findable on the
 * picture. A pin is a dot on the very spot, with its number just above and to the right,
 * so the word it points at stays readable — to the engineer, and to an agent.
 */
function badgeOf(mark, width, color) {
    const r = width * 5.5;
    const at = anchorOf(mark.geometry);
    const pin = mark.geometry.kind === 'pin';
    const cx = pin ? at.x + r * 1.3 : at.x - r * 0.4;
    const cy = pin ? at.y - r * 1.3 : at.y - r * 0.4;
    const pointer = pin
        ? `<path d="M${n(at.x)} ${n(at.y)} L${n(cx)} ${n(cy)}" stroke="${HALO}" stroke-width="${n(width * 1.8)}" opacity="0.85"/>` +
            `<path d="M${n(at.x)} ${n(at.y)} L${n(cx)} ${n(cy)}" stroke="${color}" stroke-width="${n(width * 0.8)}"/>` +
            `<circle cx="${n(at.x)}" cy="${n(at.y)}" r="${n(width * 1.6)}" fill="${color}" stroke="${HALO}" stroke-width="${n(width * 0.7)}"/>`
        : '';
    return (`<g data-mark-id="${esc(mark.id)}" class="annotate-badge">` +
        pointer +
        `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}" fill="${color}" stroke="${HALO}" stroke-width="${n(width * 0.8)}"/>` +
        `<text x="${n(cx)}" y="${n(cy)}" text-anchor="middle" dominant-baseline="central" font-family="system-ui, sans-serif" ` +
        `font-weight="700" font-size="${n(r * 1.1)}" fill="${HALO}">${mark.number}</text>` +
        `</g>`);
}
/** Every mark, as the inside of an <svg> whose viewBox is the image's pixels. */
export function marksSvg(marks, options) {
    const width = lineWidth(options);
    const parts = marks.map((m) => {
        const color = m.id === options.selected ? SELECTED_COLOR : MARK_COLOR;
        return `<g data-mark="${esc(m.id)}">${shapeOf(m.geometry, width, color)}${badgeOf(m, width, color)}</g>`;
    });
    return parts.join('');
}
/** A whole SVG document of the marks alone, the snapshot's size: for compositing an export. */
export function standaloneSvg(marks, options) {
    return (`<svg xmlns="http://www.w3.org/2000/svg" width="${options.width}" height="${options.height}" ` +
        `viewBox="0 0 ${options.width} ${options.height}">${marksSvg(marks, options)}</svg>`);
}
/** What is being drawn right now, before it is a mark: the same look, dimmer. */
export function draftSvg(geometry, options) {
    if (!geometry)
        return '';
    const width = lineWidth(options);
    if (geometry.kind === 'pin')
        return '';
    return `<g opacity="0.7">${shapeOf(geometry, width, MARK_COLOR)}</g>`;
}
/** The outline of the element a tap would pin, while hovering in Annotate. */
export function hoverSvg(box, options) {
    if (!box)
        return '';
    const width = Math.max(1, lineWidth(options) / 2);
    return `<rect x="${n(box.x)}" y="${n(box.y)}" width="${n(box.w)}" height="${n(box.h)}" fill="${SELECTED_COLOR}" fill-opacity="0.08" stroke="${SELECTED_COLOR}" stroke-width="${width}"/>`;
}
//# sourceMappingURL=render.js.map